// Generic Gallery API util — one function for every continent/country.
// Uses CRA's proxy, so calls are relative (to /api).

/** Convert "South America" or "south-america" -> "south-america" */
export function slugify(input = "") {
  return String(input)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Parse a pathname like "/africa/madagascar-wildlife" into slugs */
export function parseGalleryPath(pathname = "") {
  const parts = String(pathname).replace(/^\/+|\/+$/g, "").split("/");
  return {
    continent: parts[0] ? slugify(parts[0]) : null,
    country: parts[1] ? slugify(parts[1]) : null,
  };
}

/**
 * Fetch media by slugs.
 * @param {Object} params
 * @param {string} params.continent - slug, e.g. "africa"
 * @param {string} [params.country] - slug, e.g. "madagascar-wildlife"
 * @returns {Promise<{items:any[]}>}
 */
export async function fetchGallery({ continent, country } = {}) {
  const c = slugify(continent || "");
  const k = country ? slugify(country) : null;
  if (!c) throw new Error("fetchGallery: 'continent' is required");

  const qs = k ? `?country=${encodeURIComponent(k)}` : "";
  const primaryUrl = `/api/galleries/${encodeURIComponent(c)}${qs}`;
  const legacyUrl  = `/api/gallery/${encodeURIComponent(c)}${qs}`; // temporary fallback

  // Always ask for JSON
  const opts = { headers: { Accept: "application/json" } };

  // Try the new plural endpoint first
  let res = await fetch(primaryUrl, opts);
  if (res.ok) return res.json();

  // Optional legacy fallback if the new route isn't mounted yet
  if (res.status === 404) {
    const res2 = await fetch(legacyUrl, opts);
    if (res2.ok) return res2.json();
  }

  // Surface useful error text in dev
  const text = await res.text().catch(() => "");
  throw new Error(
    `Gallery fetch failed (${res.status}): ${text?.slice(0, 200) || "No response body"}`
  );
}

/**
 * Convenience: fetch based on the current URL path.
 * Example: fetchGalleryForPath(location.pathname)
 */
export function fetchGalleryForPath(pathname = "") {
  const { continent, country } = parseGalleryPath(pathname);
  return fetchGallery({ continent, country });
}

/** Optional: admin/testing — list everything */
export async function fetchAllGalleries() {
  const res = await fetch("/api/galleries", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Failed to fetch all galleries (${res.status})`);
  return res.json(); // { items: [...] }
}

